import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { authSchema, type AuthForm } from "../model/schema";
import { ApiError, api, storeTokens } from "@shared/api";
import { Button } from "@shared/ui/button";
import { Input } from "@shared/ui/input";
import { countdown } from "@shared/lib/format";
import { Mark, Word } from "@shared/ui/Brand";
import { Field, Notice, useTick } from "@shared/ui/bits";
import { useSession } from "@entities/session";
import { isDesktop, useRestartBlocker } from "@shared/lib/desktop";
import { DESKTOP_DOWNLOAD, DESKTOP_DOWNLOAD_LINUX } from "@shared/config";
import { authRestartBlocker } from "../model/restart";


/** Отказ от сервера, разобранный до того, что можно показать человеку. */
interface Refusal {
  kind: "warn" | "bad" | "plain";
  title: string;
  body: string;
  /** Пока он не истёк, кнопка входа выключена. */
  until: Date | null;
}

const RESTRICTIONS: Record<string, { title: string; body: string }> = {
  account_suspended: {
    title: "Учётная запись заблокирована",
    body: "Пароль верный — дело не в нём. Блокировка снимется сама, когда истечёт срок.",
  },
  account_banned: {
    title: "Учётная запись забанена",
    body: "Вход закрыт бессрочно. Единственное действие — написать в поддержку.",
  },
  account_spam: {
    title: "Учётная запись помечена спамом",
    body: "Вход закрыт бессрочно. Единственное действие — написать в поддержку.",
  },
  account_deleted: {
    title: "Учётная запись удалена",
    body: "Восстановить её отсюда нельзя.",
  },
};

function refusalOf(error: unknown): Refusal {
  if (!(error instanceof ApiError)) {
    return {
      kind: "bad",
      title: "Сервер не ответил",
      body: "Проверьте соединение и попробуйте ещё раз.",
      until: null,
    };
  }

  if (error.code === "auth_locked_out") {
    return {
      kind: "warn",
      title: "Вход закрыт",
      body:
        "Слишком много неудачных попыток подряд. Задержка растёт: минута, пять, час. " +
        "Пароль тут ни при чём — правильный сейчас тоже не подойдёт.",
      until: error.until,
    };
  }

  const restriction = error.code ? RESTRICTIONS[error.code] : undefined;
  if (restriction) {
    return { kind: "bad", ...restriction, until: error.until };
  }

  if (error.code === "invite_required") {
    return {
      kind: "warn",
      title: "Нужен код приглашения",
      body: "Учётные записи здесь заводят по приглашению. Попросите ссылку у того, кто вас позвал.",
      until: null,
    };
  }

  if (error.code === "invite_invalid") {
    return {
      kind: "warn",
      title: "Код не подошёл",
      // Три причины намеренно неразличимы: по разнице ответов код
      // подбирался бы вдвое быстрее.
      body: "Либо в нём опечатка, либо он уже исчерпан или истёк. Попросите новую ссылку.",
      until: null,
    };
  }

  if (error.code === "auth_invalid_credentials") {
    return { kind: "plain", title: "Неверная почта или пароль", body: "", until: null };
  }

  return { kind: "bad", title: "Не получилось", body: error.message, until: null };
}

export function Auth() {
  const { signIn } = useSession();
  const [refusal, setRefusal] = useState<Refusal | null>(null);
  const [handle, setHandle] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<AuthForm>({
    resolver: zodResolver(authSchema),
    defaultValues: {
      mode: "login",
      email: "",
      password: "",
      username: "",
      displayName: "",
      /*
       * Код приглашения берётся из ссылки, если он там есть.
       *
       * Приглашённому дают одну ссылку, и переписывать из неё код руками —
       * лишнее действие ровно в тот момент, когда человек первый раз видит
       * приложение. Поле остаётся видимым: код можно вписать и вручную, если
       * ссылку передали голосом.
       */
      invite: inviteFromLink(),
    },
  });

  const mode = watch("mode");
  useRestartBlocker(authRestartBlocker(isDirty, isSubmitting));

  // Обратный отсчёт идёт на месте кнопки, поэтому экран должен тикать.
  const locked = refusal?.until != null && refusal.until.getTime() > Date.now();
  useTick(locked);

  const stillLocked = refusal?.until != null && refusal.until.getTime() > Date.now();

  /*
   * Отказ сервера остаётся отдельным блоком, а не ложится на поле: почти все
   * его причины — про учётную запись целиком, а не про то, что набрано.
   * «Неверная почта или пароль» тем более: сервер намеренно не говорит, что
   * из двух, и раскладывать это по полям значило бы подсказывать подбор.
   */
  const submit = handleSubmit(async (form) => {
    if (stillLocked) return;
    setRefusal(null);

    try {
      if (form.mode === "register") {
        const created = await api.auth.register({
          username: form.username,
          email: form.email,
          password: form.password,
          display_name: form.displayName || form.username,
          invite: form.invite.trim() || undefined,
        });
        setHandle(created.handle);
        // Регистрация не входит сама: пароль уже набран, вход занимает
        // мгновение, а хэндл нужно показать до того, как экран сменится.
        storeTokens(await api.auth.login(form.email, form.password));
      }

      await signIn(form.email, form.password);
    } catch (error) {
      setRefusal(refusalOf(error));
      setHandle(null);
    }
  });

  return (
    <div className="auth">
      {/*
        Проверку ведёт схема, а не браузер: у встроенной свои формулировки и
        свой момент показа, и вдвоём они спорят — на неверной почте браузер
        отбивал отправку раньше, чем схема успевала сказать хоть слово.
      */}
      {/* Материал «Почты», без стекла движка — как окна. */}
      <form className="auth-card surface-pop" onSubmit={submit} noValidate>
        <div className="auth-brand">
          <Mark size="auth" />
          <div className="auth-brand-line">
            <Word />
            <span className="auth-brand-mode">{mode === "login" ? "вход" : "регистрация"}</span>
          </div>
        </div>

        <div className="auth-title">
          {mode === "login" ? "С возвращением" : "Заведём учётную запись"}
        </div>

        {mode === "register" && (
          <>
            <Field
              label="Имя"
              hint="Не уникально — к нему выдаётся четырёхзначный номер"
              error={errors.username?.message}
            >
              <Input autoComplete="username" {...register("username")} />
            </Field>
            <Field
              label="Отображаемое имя"
              hint="Произвольное, его и видят в ленте"
              error={errors.displayName?.message}
            >
              <Input {...register("displayName")} />
            </Field>
            <Field label="Код приглашения" hint="Из ссылки, по которой вас позвали">
              <Input
                spellCheck={false}
                autoCapitalize="off"
                {...register("invite")}
              />
            </Field>
          </>
        )}

        <Field label="Почта" error={errors.email?.message}>
          <Input type="email" autoComplete="email" {...register("email")} />
        </Field>

        <Field
          label="Пароль"
          hint={mode === "register" ? "От восьми знаков" : undefined}
          error={errors.password?.message}
        >
          <Input
            type="password"
            autoComplete={mode === "login" ? "current-password" : "new-password"}
            // Поле остаётся видимым, но неактивным: понятно, что пароль ни при чём.
            disabled={stillLocked}
            {...register("password")}
          />
        </Field>

        {refusal && refusal.kind === "plain" && (
          <div className="small danger-text">{refusal.title}</div>
        )}

        {refusal && refusal.kind !== "plain" && (
          <Notice kind={refusal.kind} title={refusalTitle(refusal)}>
            {refusal.body}
          </Notice>
        )}

        <Button type="submit" className="w-full" disabled={isSubmitting || stillLocked}>
          {stillLocked && refusal?.until
            ? `Ещё ${countdown(refusal.until)}`
            : isSubmitting
              ? "Секунду…"
              : mode === "login"
                ? "Войти"
                : "Создать"}
        </Button>

        <div className="auth-foot">
          {mode === "login" ? "Нет учётной записи?" : "Уже есть?"}
          <button
            type="button"
            className="link-btn"
            onClick={() => {
              setValue("mode", mode === "login" ? "register" : "login");
              setRefusal(null);
            }}
          >
            {mode === "login" ? "Регистрация" : "Вход"}
          </button>
          {/* Восстановления пароля в API нет — обещать его кнопкой нечестно. */}
          <span className="plan-tag push">восстановление — план</span>
        </div>

        {/* В приложении ссылка на само приложение ни к чему. */}
        {!isDesktop() && (
          <div className="auth-foot">
            Есть приложение —
            <a href={DESKTOP_DOWNLOAD} download>
              для Windows
            </a>
            и
            <a href={DESKTOP_DOWNLOAD_LINUX} download>
              для Linux
            </a>
          </div>
        )}

        {handle ? (
          <div className="auth-note">
            Ваш хэндл — <span className="mono" style={{ color: "var(--ui-text-body)" }}>{handle}</span>.
            По нему вас найдут для заявки в друзья; в ленте он не показывается.
          </div>
        ) : (
          mode === "register" && (
            <div className="auth-note">
              Имя не уникально: к нему выдаётся четырёхзначный номер, и хэндл получается
              вида <span className="mono" style={{ color: "var(--ui-text-body)" }}>alex#0042</span>.
              Он будет виден сразу после создания.
            </div>
          )
        )}
      </form>
    </div>
  );
}

function refusalTitle(refusal: Refusal): string {
  if (!refusal.until) return refusal.title;
  if (refusal.until.getTime() <= Date.now()) return `${refusal.title} — срок истёк, попробуйте снова`;
  return `${refusal.title} · ещё ${countdown(refusal.until)}`;
}

/**
 * Код приглашения из ссылки.
 *
 * Читаются оба вида — `?invite=код` и `#/invite/код`: первый удобно
 * скопировать, второй получается сам, если ссылкой поделились из
 * приложения. Чужого сюда попасть не может: код всё равно проверяется
 * сервером, а негодный отвергается до создания учётной записи.
 */
function inviteFromLink(): string {
  if (typeof window === "undefined") return "";

  const query = new URLSearchParams(window.location.search).get("invite");
  if (query) return query.trim();

  const hash = window.location.hash.match(/invite\/([^/?#]+)/);
  return hash ? hash[1].trim() : "";
}
